import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { SuggestedPetition } from '../../../../../../../../shared/api';
import { useSuggestedPetitionsTracking } from '../../../../../../../../shared/hooks/useSuggestedPetitionsTracking';
import type { SuggestedPetitionSourceLocation } from '../../../../../../../../shared/types';
import { generatePetitionUrl } from '../../../../../../../../shared/utils';

import { usePetitionImageUrl } from './usePetitionImageUrl';
import { useSignOnClick } from './useSignOnClick';

type Result = ModelHookResult<
	{
		imageUrl?: string;
		isVictory: boolean;
		location: string;
		petitionId: string;
		petitionLinkUrl: string;
		promoterCount: number;
		promoterCountLocalized: string;
		promotersLinkUrl?: string;
		signLinkUrl?: string;
		supporterCount: number;
		supporterCountLocalized: string;
		title: string;
	},
	{
		signLinkOnClick: () => void;
		petitionLinkOnClick: () => void;
		promotersLinkOnClick?: () => void;
	}
>;

type Options = {
	parentPetitionId?: string;
	petition: SuggestedPetition;
	gridPosition: number;
	sourceLocation: SuggestedPetitionSourceLocation;
};

/**
 * This provides the ds2-equivalent implementation of useSuggestedPetitionCard.
 */
// eslint-disable-next-line max-lines-per-function
export function useSuggestedPetitionCardDs2({
	petition,
	parentPetitionId,
	sourceLocation,
	gridPosition,
}: Options): Result {
	const petitionsUrls = generatePetitionUrl({ petition, gridPosition, parentPetitionId, sourceLocation });
	const {
		actions: { trackClick },
	} = useSuggestedPetitionsTracking({
		parentPetitionId,
		petitionStatus: 'open',
	});

	const {
		node: {
			id: petitionId,
			promotionStats,
			signatureState: { signatureCount },
			displayTitle,
			relevantLocationLocalizedName,
			country,
		},
	} = petition;

	const imageUrl = usePetitionImageUrl(petition);

	const {
		data: { canOneClickSign },
		actions: { signLinkSignNavigateOnClick, signLinkTrackOnClick },
	} = useSignOnClick({ petition, parentPetitionId, gridPosition });

	const { getCountry } = useI18n();

	const petitionLinkOnClick = useCallback(
		() => trackClick('title', petition, gridPosition),
		[gridPosition, petition, trackClick],
	);

	return {
		data: {
			// DS2 does not handle the empty-string placeholder state.
			imageUrl: imageUrl === '' ? undefined : imageUrl,
			// The suggested petitions query excludes victories.
			isVictory: false,
			location: relevantLocationLocalizedName || getCountry(country.countryCode)?.name || '',
			petitionId,
			petitionLinkUrl: petitionsUrls.petitionUrl,
			promoterCount: promotionStats?.totalCount ?? 0,
			promoterCountLocalized: promotionStats?.totalCountLocalized ?? '0',
			promotersLinkUrl: petitionsUrls.petitionPromotersUrl,
			signLinkUrl: canOneClickSign ? undefined : petitionsUrls.petitionSignUrl,
			supporterCount: signatureCount.displayed,
			supporterCountLocalized: signatureCount.displayedLocalized,
			title: displayTitle,
		},
		actions: {
			signLinkOnClick: canOneClickSign ? signLinkSignNavigateOnClick : signLinkTrackOnClick,
			petitionLinkOnClick,
			// The DS1 card didn't track clicks on this link.
			promotersLinkOnClick: undefined,
		},
	};
}
